import React from "react";
import { DayOfWeek } from "../types";

// Form Input Component
interface FormInputProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
}

export const FormInput: React.FC<FormInputProps> = ({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
}) => {
  return (
    <div className="mb-5">
      <label
        htmlFor={id}
        className="block text-[18px] font-medium text-neutral-700 dark:text-white/60 mb-2"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className="modern-input disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:text-neutral-400"
      />
    </div>
  );
};

// Form Textarea Component
interface FormTextareaProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
}

export const FormTextarea: React.FC<FormTextareaProps> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required = false,
  rows = 4,
}) => {
  return (
    <div className="mb-5">
      <label
        htmlFor={id}
        className="block text-[18px] font-medium text-neutral-700 dark:text-white/60 mb-2"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        rows={rows}
        className="w-full px-4 py-3.5 rounded-xl border border-black/[0.1] bg-black/[0.04] placeholder:text-gray-400 text-neutral-900 focus:outline-none focus:border-[#f5a623] transition-colors duration-150 resize-none dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-neutral-100 dark:placeholder:text-white/25"
      />
    </div>
  );
};

// Form Select Component
interface FormSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export const FormSelect: React.FC<FormSelectProps> = ({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  placeholder = "Select an option",
  disabled = false,
}) => {
  return (
    <div className="mb-5">
      <label
        htmlFor={id}
        className="block text-[18px] font-medium text-neutral-700 dark:text-white/60 mb-2"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled}
        className={`w-full px-4 py-3.5 rounded-xl border border-black/[0.1] bg-black/[0.04] text-neutral-900 focus:outline-none focus:border-[#f5a623] transition-colors duration-150 dark:border-white/[0.1] dark:bg-white/[0.06] dark:text-neutral-100 ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

// Form Checkbox Component
interface FormCheckboxProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description?: string;
}

export const FormCheckbox: React.FC<FormCheckboxProps> = ({
  id,
  label,
  checked,
  onChange,
  description,
}) => {
  return (
    <div className="mb-5">
      <div className="flex items-start">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 text-[#f5a623] bg-black/[0.04] border-black/[0.15] rounded focus:ring-[#f5a623] focus:ring-2 mt-1 transition-all duration-150 dark:bg-white/[0.06] dark:border-white/[0.2]"
        />
        <div className="ml-3">
          <label
            htmlFor={id}
            className="block text-[19px] font-medium text-neutral-800 dark:text-neutral-100"
          >
            {label}
          </label>
          {description && (
            <p className="text-[17px] text-neutral-500 dark:text-white/45 mt-1">
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Day Selector Component
interface DaySelectorProps {
  selectedDays: DayOfWeek[];
  onDayToggle: (day: DayOfWeek) => void;
  label?: string;
}

export const DaySelector: React.FC<DaySelectorProps> = ({
  selectedDays,
  onDayToggle,
  label = "Active Days",
}) => {
  const days: DayOfWeek[] = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const dayLabels = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
  };

  return (
    <div className="mb-5">
      <label className="block text-[18px] font-medium text-neutral-700 dark:text-white/60 mb-3">
        {label}
      </label>
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const isSelected = selectedDays.includes(day);
          return (
            <button
              key={day}
              type="button"
              onClick={() => onDayToggle(day)}
              className={`p-3 rounded-xl border transition-all duration-150 touch-manipulation ${
                isSelected
                  ? "bg-[#f5a623]/15 text-[#f5a623] border-[#f5a623]/40"
                  : "bg-white dark:bg-white/[0.04] border-black/[0.08] dark:border-white/[0.08] text-neutral-700 dark:text-white/45 hover:bg-black/[0.04] dark:hover:bg-white/[0.09]"
              }`}
            >
              <div className="text-xs font-semibold text-center">
                {dayLabels[day as keyof typeof dayLabels]}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-[17px] text-neutral-500 dark:text-white/45 mt-2">
        Select which days this habit should be active
      </p>
    </div>
  );
};

// Form Button Component
interface FormButtonProps {
  type?: "button" | "submit" | "reset";
  variant?: "primary" | "secondary" | "danger";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

export const FormButton: React.FC<FormButtonProps> = ({
  type = "button",
  variant = "primary",
  size = "md",
  disabled = false,
  loading = false,
  onClick,
  className = "",
  children,
}) => {
  const baseClasses =
    "inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 touch-manipulation";

  const variantClasses = {
    primary:
      "bg-[#f5a623] hover:opacity-90 active:opacity-75 focus:ring-[#f5a623] text-[#1a1300] border border-[#f5a623]",
    secondary:
      "bg-white hover:bg-black/[0.04] dark:bg-white/[0.04] dark:hover:bg-white/[0.09] focus:ring-white/30 text-neutral-700 dark:text-white/70 border border-black/[0.08] dark:border-white/[0.1]",
    danger:
      "bg-[#e23f5d] hover:opacity-90 active:opacity-75 focus:ring-[#e23f5d] text-white border border-[#e23f5d]",
  };

  const sizeClasses = {
    sm: "px-4 py-2 text-[19px]",
    md: "px-5 py-2.5 text-[20px]",
    lg: "px-6 py-3 text-[20px]",
  };

  const disabledClasses = "disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${disabledClasses} ${className}`}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {children}
    </button>
  );
};
